import Footer from '../components/Footer';
import LandingHeader from '../components/LandingHeader';

const PrivacyPolicy = () => {
  const handleSignIn = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <LandingHeader onSignIn={handleSignIn} />

      {/* Content */}
      <div className="bg-white py-4 sm:py-6 md:py-8">
        <main className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6 sm:mb-8 md:mb-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 tracking-tight">Privacy Policy</h1>
            <p className="text-sm sm:text-base text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-8 md:p-10 space-y-8 sm:space-y-10">
          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
              Welcome to Inbox Mate. We respect your privacy and are committed to protecting your personal data.
              This privacy policy explains how we collect, use, and safeguard your information when you use our service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">2. Information We Collect</h2>
            <div className="space-y-4 sm:space-y-6">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">2.1 Information You Provide</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 text-base sm:text-lg leading-relaxed">
                  <li>Email address and name (via Google OAuth)</li>
                  <li>Gmail messages and metadata you choose to process</li>
                  <li>Settings and preferences you configure</li>
                  <li>Knowledgebase content you create</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">2.2 Automatically Collected Information</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 text-base sm:text-lg leading-relaxed">
                  <li>Usage data and interaction with our service</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">3. How We Use Your Information</h2>
            <p className="text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 text-base sm:text-lg leading-relaxed">
              <li>Provide and maintain our email management service</li>
              <li>Process and organize your Gmail messages</li>
              <li>Generate AI-powered email responses (Pro plan)</li>
              <li>Improve and optimize our service</li>
              <li>Send you important updates and notifications</li>
              <li>Provide customer support</li>
              <li>Process payments and subscriptions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">4. Data Storage and Security</h2>
            <p className="text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">
              We take data security seriously and implement appropriate measures to protect your information:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 text-base sm:text-lg leading-relaxed">
              <li>All data is stored securely using Firebase (Google Cloud Platform)</li>
              <li>Communication is encrypted using SSL/TLS</li>
              <li>Access tokens are stored in session storage and expire after your session</li>
              <li>We do not store your Gmail password</li>
              <li>Your knowledgebase and settings are stored in Firebase Firestore</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">5. Gmail API Access</h2>
            <p className="text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">
              Inbox Mate's use and transfer of information received from Google APIs adheres to the{' '}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline font-medium"
              >
                Google API Services User Data Policy
              </a>, including the Limited Use requirements.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">
              We access your Gmail only to provide the core functionality of our service. We do not:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 text-base sm:text-lg leading-relaxed">
              <li>Share your Gmail data with third parties</li>
              <li>Use your emails for advertising</li>
              <li>Read or access emails you haven't explicitly chosen to process</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">6. Third-Party Services</h2>
            <p className="text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">We use the following third-party services:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 text-base sm:text-lg leading-relaxed">
              <li><strong className="text-gray-900">Google OAuth & Gmail API:</strong> For authentication and email access</li>
              <li><strong className="text-gray-900">Firebase:</strong> For data storage and authentication</li>
              <li><strong className="text-gray-900">Stripe:</strong> For payment processing</li>
              <li><strong className="text-gray-900">OpenAI:</strong> For AI-powered email response generation (Pro plan only)</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4 text-base sm:text-lg">
              Each of these services has their own privacy policies governing how they handle your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">7. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 text-base sm:text-lg leading-relaxed">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your data</li>
              <li>Export your data</li>
              <li>Revoke Gmail access at any time through your Google Account settings</li>
              <li>Unsubscribe from our service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">8. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
              We retain your data only as long as necessary to provide our services. Your knowledgebase and settings
              are stored in Firebase and can be deleted at any time. Session data is cleared when you log out or
              your session expires.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">9. Cookies and Tracking</h2>
            <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
              We use session storage to maintain your login state. We do not use cookies for tracking or advertising purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">10. Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
              Our service is not intended for users under 13 years of age. We do not knowingly collect data from children.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">11. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
              We may update this privacy policy from time to time. We will notify you of any changes by posting the
              new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight">12. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
              If you have any questions about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:support@gmailboard.com" className="text-blue-600 hover:text-blue-800 underline font-medium">
                support@gmailboard.com
              </a>
            </p>
          </section>
        </div>

        {/* Back to top */}
        <div className="mt-8 sm:mt-10 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm sm:text-base"
          >
            Back to top ↑
          </button>
        </div>
        </main>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
